import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";
import { CardGiftcard, LocationOn, Store } from "@mui/icons-material";
import { dateFormatter, maskPhone } from "../utils/masking";

export interface GiftCardItem {
  accountId: string;
  balance: number;
  cardNumber: string;
  cardSuffix: string;
  createdAt: string;
  default: boolean;
  updatedAt: string;
}

export interface AddressItem {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  country: string | null;
  favorite: boolean;
  latitude: number;
  longitude: number;
  postalCode: string;
  state: string;
}

export interface FavoriteStoreItem {
  isDefault: boolean;
  storeId: string;
  storeName: string;
}

type Customer = {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhoneNumber?: string;
  signUpDate?: string;
  zaxbysEnabled?: boolean;
  oloAuthToken?: string;
};

type Props = {
  customer: Customer | undefined;
  giftCardsList: GiftCardItem[];
  addressesList: AddressItem[];
  favoriteStoresList: FavoriteStoreItem[];
  buttonStyle: object;
  onOpenGiftCards: () => void;
  onOpenAddresses: () => void;
  onOpenFavoriteStores: () => void;
  onUpdate: () => void;
  onDelete: () => void;
};

export function ZaxbysAdminCustomerCard({
  customer,
  giftCardsList,
  addressesList,
  favoriteStoresList,
  buttonStyle,
  onOpenGiftCards,
  onOpenAddresses,
  onOpenFavoriteStores,
  onUpdate,
  onDelete,
}: Readonly<Props>) {
  if (!customer) {
    return (
      <Typography variant="body1" color="text.secondary">
        No customer data available
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Name:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {customer.firstName} {customer.lastName}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Email:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {customer.email}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Phone:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {maskPhone(customer.mobilePhoneNumber ?? "")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Rewards Signup Date:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {customer.signUpDate ? dateFormatter(customer.signUpDate) : "N/A"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Zaxby Account Status:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {customer.zaxbysEnabled ? "Enabled" : "Disabled"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Olo Account Associated:
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "right" }}>
          {customer.oloAuthToken ? "Yes" : "No"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Address{addressesList.length > 1 ? "es" : ""}:
        </Typography>
        {addressesList.length > 0 ? (
          <Button
            variant="text"
            size="small"
            onClick={onOpenAddresses}
            startIcon={<LocationOn sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              color: "#1976d2",
              textAlign: "right",
              "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
            }}
          >
            {addressesList[0].addressLine1}, {addressesList[0].city}, {addressesList[0].state}
            {addressesList.length > 1 && ` (+${addressesList.length - 1} more)`}
          </Button>
        ) : (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            N/A
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Favorite Store{favoriteStoresList.length > 1 ? "s" : ""}:
        </Typography>
        {favoriteStoresList.length > 0 ? (
          <Button
            variant="text"
            size="small"
            onClick={onOpenFavoriteStores}
            startIcon={<Store sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              color: "#1976d2",
              textAlign: "right",
              "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
            }}
          >
            {favoriteStoresList[0].storeName}
            {favoriteStoresList.length > 1 && ` (+${favoriteStoresList.length - 1} more)`}
          </Button>
        ) : (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            N/A
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Gift Cards:
        </Typography>
        {giftCardsList.length > 0 ? (
          <Button
            variant="text"
            size="small"
            onClick={onOpenGiftCards}
            startIcon={<CardGiftcard sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              color: "#1976d2",
              "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
            }}
          >
            View {giftCardsList.length} gift card{giftCardsList.length === 1 ? "" : "s"}
          </Button>
        ) : (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            N/A
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
        <Button variant="contained" sx={buttonStyle} onClick={onUpdate}>
          UPDATE
        </Button>
        <Button
          variant="contained"
          sx={{ ...buttonStyle, backgroundColor: "#b71c1c", "&:hover": { backgroundColor: "#7f0000" } }}
          onClick={onDelete}
        >
          DELETE
        </Button>
      </Box>
    </Stack>
  );
}

export function ZaxbysAdminCardShell({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Card
      sx={{
        border: "3px solid rgba(0,0,0,0.1)",
        borderRadius: 2,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        minWidth: "400px",
        maxWidth: "600px",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
          Customer Account
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}
